import SETTING_KEYS from '../../../constants/setting-keys';
import type { PolicyConditionalsValue } from './types';

const ensureId = <T extends { id?: string }>(
    item: T,
    prefix: string,
    index: number
): T & { id: string } => ({
    ...item,
    id: item.id ?? `${prefix}-${index}`,
});

export const createEmptyPolicyConditionalsValue =
    (): PolicyConditionalsValue => ({
        [SETTING_KEYS.addInfo]: '',
        [SETTING_KEYS.prep]: '',
        [SETTING_KEYS.fieldStrength]: [],
        [SETTING_KEYS.mriArc]: '',
        [SETTING_KEYS.waitingPeriod]: {},
        [SETTING_KEYS.spatialGradient]: {
            gradients: [],
        },
        [SETTING_KEYS.slewRate]: {},
        [SETTING_KEYS.sar]: [],
        [SETTING_KEYS.brms]: '',
        [SETTING_KEYS.coilType]: '',
        [SETTING_KEYS.temp]: [],
        [SETTING_KEYS.artefacts]: [],
    });

export const normalizePolicyConditionalsValue = (
    value?: PolicyConditionalsValue
): PolicyConditionalsValue | undefined => {
    if (!value) return undefined;

    return {
        [SETTING_KEYS.addInfo]: value[SETTING_KEYS.addInfo] ?? '',
        [SETTING_KEYS.prep]: value[SETTING_KEYS.prep] ?? '',
        [SETTING_KEYS.fieldStrength]: value[SETTING_KEYS.fieldStrength] ?? [],
        [SETTING_KEYS.mriArc]: value[SETTING_KEYS.mriArc] ?? '',
        [SETTING_KEYS.waitingPeriod]: value[SETTING_KEYS.waitingPeriod] ?? {},
        [SETTING_KEYS.spatialGradient]: {
            text: value[SETTING_KEYS.spatialGradient]?.text,
            gradients: (value[SETTING_KEYS.spatialGradient]?.gradients ?? [])
                .map((g, i) => ensureId(g, 'gradient', i)),
        },
        [SETTING_KEYS.slewRate]: value[SETTING_KEYS.slewRate] ?? {},
        [SETTING_KEYS.sar]: (value[SETTING_KEYS.sar] ?? [])
            .map((s, i) => ensureId(s, 'sar', i)),
        [SETTING_KEYS.brms]: value[SETTING_KEYS.brms] ?? '',
        [SETTING_KEYS.coilType]: value[SETTING_KEYS.coilType] ?? '',
        [SETTING_KEYS.temp]: (value[SETTING_KEYS.temp] ?? [])
            .map((t, i) => ensureId(t, 'temp', i)),
        [SETTING_KEYS.artefacts]: (value[SETTING_KEYS.artefacts] ?? [])
            .map((a, i) => ensureId(a, 'artefact', i)),
    };
};

export const hasPolicyConditionalsContent = (
    value?: PolicyConditionalsValue
): boolean => {
    const normalized = normalizePolicyConditionalsValue(value);
    if (!normalized) return false;

    return [
        normalized[SETTING_KEYS.addInfo],
        normalized[SETTING_KEYS.prep],
        normalized[SETTING_KEYS.fieldStrength]?.length,
        normalized[SETTING_KEYS.mriArc],
        normalized[SETTING_KEYS.waitingPeriod]?.immediateScan,
        normalized[SETTING_KEYS.waitingPeriod]?.weeksAfterScan,
        normalized[SETTING_KEYS.spatialGradient]?.text,
        normalized[SETTING_KEYS.spatialGradient]?.gradients?.length,
        normalized[SETTING_KEYS.slewRate]?.rate,
        normalized[SETTING_KEYS.slewRate]?.condition,
        normalized[SETTING_KEYS.sar]?.length,
        normalized[SETTING_KEYS.brms],
        normalized[SETTING_KEYS.coilType],
        normalized[SETTING_KEYS.temp]?.length,
        normalized[SETTING_KEYS.artefacts]?.length,
    ].some(Boolean);
};

export const toPersistedPolicyConditionals = (
    value?: PolicyConditionalsValue
): PolicyConditionalsValue | undefined => {
    const normalized = normalizePolicyConditionalsValue(value);
    if (!normalized || !hasPolicyConditionalsContent(normalized)) {
        return undefined;
    }

    return {
        ...normalized,
        [SETTING_KEYS.spatialGradient]: normalized[
            SETTING_KEYS.spatialGradient
        ]
            ? {
                  text: normalized[SETTING_KEYS.spatialGradient]?.text,
                  gradients: (
                      normalized[SETTING_KEYS.spatialGradient]?.gradients ?? []
                  ).map(({ id, ...item }) => item),
              }
            : undefined,
        [SETTING_KEYS.sar]: (normalized[SETTING_KEYS.sar] ?? []).map(
            ({ id, ...item }) => item
        ),
        [SETTING_KEYS.temp]: (normalized[SETTING_KEYS.temp] ?? []).map(
            ({ id, ...item }) => item
        ),
        [SETTING_KEYS.artefacts]: (
            normalized[SETTING_KEYS.artefacts] ?? []
        ).map(({ id, ...item }) => item),
    };
};

export const stripConditionalsFromDocumentData = (
    data: Record<string, any>
) => {
    const { policyConditionals, ...rest } = data;
    return rest;
};

export const mergePersistedConditionalsIntoDocumentData = (
    data: Record<string, any> | undefined,
    conditionals?: PolicyConditionalsValue
) => {
    if (!conditionals) return data ?? {};
    return { ...(data ?? {}), policyConditionals: conditionals };
};
